var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useReducer, useRef } from 'react';
import { makeStyles } from "../../../../index";
import { getRadianAngle } from "../utils/getRadianAngle";
import { TouchEventWrapper } from "../utils/TouchEventWrapper";
var useStyles = makeStyles(function () { return ({
    root: {
        position: 'relative',
    },
    canvas: {
        position: 'absolute',
    },
    outputCanvas: {
        display: 'none',
    },
}); });
var initialState = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    rotate: 0,
    environment: {
        width: 0,
        height: 0,
        rotate: 0,
    },
};
var cropperReducer = function (state, action) {
    switch (action.type) {
        case 'environment': {
            var dx = state.environment.width === 0 ? 1 : action.payload.width / state.environment.width;
            var dy = state.environment.height === 0 ? 1 : action.payload.height / state.environment.height;
            return __assign(__assign({}, state), { x: state.x * dx, y: state.y * dy, width: state.width ? Math.round(state.width * dx) : action.payload.width, height: state.height ? Math.round(state.height * dy) : action.payload.height, rotate: action.payload.rotate, environment: {
                    width: action.payload.width,
                    height: action.payload.height,
                    rotate: action.payload.rotate,
                } });
        }
        case 'move':
            return __assign(__assign({}, state), { x: Math.min(Math.max(action.payload.x, 0), state.environment.width - state.width), y: Math.min(Math.max(action.payload.y, 0), state.environment.height - state.height) });
        case 'resize':
            switch (action.payload.direction) {
                case 'nw':
                    return __assign(__assign({}, state), { x: action.payload.x, y: action.payload.y, width: state.width - (action.payload.x - state.x), height: state.height - (action.payload.y - state.y) });
                case 'ne':
                    return __assign(__assign({}, state), { x: state.x, y: action.payload.y, width: action.payload.x - state.x, height: state.height - (action.payload.y - state.y) });
                case 'se':
                    return __assign(__assign({}, state), { x: state.x, y: state.y, width: action.payload.x - state.x, height: action.payload.y - state.y });
                case 'sw':
                    return __assign(__assign({}, state), { x: action.payload.x, y: state.y, width: state.width - (action.payload.x - state.x), height: action.payload.y - state.y });
                default:
                    throw new Error();
            }
        default:
            throw new Error();
    }
};
var CROPPING_BOX_SIZE = 16;
var TOUCH_CROPPING_BOX_SIZE = 48;
export var ImageEditor = function (_a) {
    var maxWidth = _a.maxWidth, maxHeight = _a.maxHeight, src = _a.src, imageType = _a.imageType, rotation = _a.rotation, onCropped = _a.onCropped, onError = _a.onError;
    var root = useRef(null);
    var canvasImageLayer = useRef(null);
    var canvasCropperLayer = useRef(null);
    var canvasOutputLayer = useRef(null);
    var classes = useStyles();
    var _b = useReducer(cropperReducer, initialState), cropper = _b[0], changeCropper = _b[1];
    useEffect(function () {
        var image = new Image();
        image.onerror = function () {
            onError === null || onError === void 0 ? void 0 : onError();
        };
        image.onload = function () {
            var r = getRadianAngle(rotation);
            var sw = Math.ceil(Math.abs(image.width * Math.cos(r)) + Math.abs(image.height * Math.sin(r)));
            var sh = Math.ceil(Math.abs(image.width * Math.sin(r)) + Math.abs(image.height * Math.cos(r)));
            var scale = Math.min((maxWidth || sw) / sw, (maxHeight || sh) / sh);
            var dw = Math.round(sw * scale);
            var dh = Math.round(sh * scale);
            if (canvasImageLayer.current) {
                var context = canvasImageLayer.current.getContext('2d');
                if (context) {
                    canvasImageLayer.current.width = dw;
                    canvasImageLayer.current.height = dh;
                    // キャンバスの中心にコンテキストを移動する
                    context.translate(dw / 2, dh / 2);
                    // 回転する
                    context.rotate(r);
                    // 描画する画像に合わせてコンテキストの座標を戻す
                    context.translate(-(image.width * scale) / 2, -(image.height * scale) / 2);
                    // リサイズする
                    context.scale(scale, scale);
                    context.drawImage(image, 0, 0);
                }
            }
            if (canvasCropperLayer.current) {
                canvasCropperLayer.current.width = dw;
                canvasCropperLayer.current.height = dh;
                changeCropper({ type: 'environment', payload: { width: dw, height: dh, rotate: r } });
            }
            if (root.current) {
                root.current.style.width = dw + "px";
                root.current.style.height = dh + "px";
            }
        };
        image.src = src;
    }, [maxHeight, maxWidth, onError, rotation, src]);
    useEffect(function () {
        var _a;
        var context = (_a = canvasCropperLayer.current) === null || _a === void 0 ? void 0 : _a.getContext('2d');
        if (!context || !canvasCropperLayer.current)
            return;
        context.clearRect(0, 0, canvasCropperLayer.current.width, canvasCropperLayer.current.height);
        context.fillStyle = 'rgba(0, 0, 0, 0.7)';
        context.fillRect(0, 0, canvasCropperLayer.current.width, canvasCropperLayer.current.height);
        context.fillStyle = 'rgba(0, 0, 0, 0)';
        context.clearRect(cropper.x, cropper.y, cropper.width, cropper.height);
        context.strokeStyle = 'rgba(255, 255, 255, 1)';
        context.lineWidth = 2;
        context.setLineDash([5, 2]);
        context.strokeRect(cropper.x, cropper.y, cropper.width, cropper.height);
        context.fillStyle = 'rgba(255, 255, 255, 1)';
        context.fillRect(cropper.x - CROPPING_BOX_SIZE / 2, cropper.y - CROPPING_BOX_SIZE / 2, CROPPING_BOX_SIZE, CROPPING_BOX_SIZE);
        context.fillRect(cropper.x + cropper.width - CROPPING_BOX_SIZE / 2, cropper.y - CROPPING_BOX_SIZE / 2, CROPPING_BOX_SIZE, CROPPING_BOX_SIZE);
        context.fillRect(cropper.x + cropper.width - CROPPING_BOX_SIZE / 2, cropper.y + cropper.height - CROPPING_BOX_SIZE / 2, CROPPING_BOX_SIZE, CROPPING_BOX_SIZE);
        context.fillRect(cropper.x - CROPPING_BOX_SIZE / 2, cropper.y + cropper.height - CROPPING_BOX_SIZE / 2, CROPPING_BOX_SIZE, CROPPING_BOX_SIZE);
    }, [cropper]);
    useEffect(function () {
        var current = canvasCropperLayer.current;
        var isMouseInCroppingArea = function (e) {
            return e.offsetX > cropper.x &&
                e.offsetX < cropper.x + cropper.width &&
                e.offsetY > cropper.y &&
                e.offsetY < cropper.y + cropper.height;
        };
        var obtainCroppingBoxOverlappedMouse = function (e) {
            var croppingBoxSize = e instanceof TouchEventWrapper ? TOUCH_CROPPING_BOX_SIZE : CROPPING_BOX_SIZE;
            var nw = e.offsetX >= cropper.x - croppingBoxSize / 2 &&
                e.offsetX <= cropper.x + croppingBoxSize / 2 &&
                e.offsetY >= cropper.y - croppingBoxSize / 2 &&
                e.offsetY <= cropper.y + croppingBoxSize / 2 &&
                'nw';
            var ne = e.offsetX >= cropper.x + cropper.width - croppingBoxSize / 2 &&
                e.offsetX <= cropper.x + cropper.width + croppingBoxSize / 2 &&
                e.offsetY >= cropper.y - croppingBoxSize / 2 &&
                e.offsetY <= cropper.y + croppingBoxSize / 2 &&
                'ne';
            var se = e.offsetX >= cropper.x + cropper.width - croppingBoxSize / 2 &&
                e.offsetX <= cropper.x + cropper.width + croppingBoxSize / 2 &&
                e.offsetY >= cropper.y + cropper.height - croppingBoxSize / 2 &&
                e.offsetY <= cropper.y + cropper.height + croppingBoxSize / 2 &&
                'se';
            var sw = e.offsetX >= cropper.x - croppingBoxSize / 2 &&
                e.offsetX <= cropper.x + croppingBoxSize / 2 &&
                e.offsetY >= cropper.y + cropper.height - croppingBoxSize / 2 &&
                e.offsetY <= cropper.y + cropper.height + croppingBoxSize / 2 &&
                'sw';
            return nw || ne || se || sw || null;
        };
        var wrapEvent = function (e) { return (e instanceof TouchEvent ? new TouchEventWrapper(e) : e); };
        var roundInRange = function (value, minValue, maxValue) {
            return Math.min(Math.max(value, minValue), maxValue);
        };
        var onMouseDown = function (e) {
            var event = wrapEvent(e);
            var croppingBox = obtainCroppingBoxOverlappedMouse(event);
            if (croppingBox) {
                var onMouseMove_1 = function (_e) {
                    if (!current)
                        return;
                    var moveEvent = wrapEvent(_e);
                    var rect = current.getBoundingClientRect();
                    changeCropper({
                        type: 'resize',
                        payload: {
                            direction: croppingBox,
                            x: roundInRange(moveEvent.clientX - rect.x, 0, rect.width),
                            y: roundInRange(moveEvent.clientY - rect.y, 0, rect.height),
                        },
                    });
                };
                var onMouseUp_1 = function () {
                    window.removeEventListener('mouseup', onMouseUp_1);
                    window.removeEventListener('mousemove', onMouseMove_1);
                    window.removeEventListener('touchend', onMouseUp_1);
                    window.removeEventListener('touchmove', onMouseMove_1);
                };
                window.addEventListener('mouseup', onMouseUp_1);
                window.addEventListener('mousemove', onMouseMove_1);
                window.addEventListener('touchend', onMouseUp_1);
                window.addEventListener('touchmove', onMouseMove_1);
            }
            else if (isMouseInCroppingArea(event)) {
                var offsetX_1 = event.clientX - cropper.x;
                var offsetY_1 = event.clientY - cropper.y;
                var onMouseMove_2 = function (_e) {
                    var moveEvent = wrapEvent(_e);
                    changeCropper({
                        type: 'move',
                        payload: { x: moveEvent.clientX - offsetX_1, y: moveEvent.clientY - offsetY_1 },
                    });
                };
                var onMouseUp_2 = function () {
                    window.removeEventListener('mouseup', onMouseUp_2);
                    window.removeEventListener('mousemove', onMouseMove_2);
                    window.removeEventListener('touchend', onMouseUp_2);
                    window.removeEventListener('touchmove', onMouseMove_2);
                };
                window.addEventListener('mouseup', onMouseUp_2);
                window.addEventListener('mousemove', onMouseMove_2);
                window.addEventListener('touchend', onMouseUp_2);
                window.addEventListener('touchmove', onMouseMove_2);
            }
        };
        var onMouseMove = function (e) {
            if (!current)
                return;
            var moveEvent = wrapEvent(e);
            switch (obtainCroppingBoxOverlappedMouse(moveEvent)) {
                case 'nw':
                case 'se':
                    current.style.cursor = 'nwse-resize';
                    break;
                case 'ne':
                case 'sw':
                    current.style.cursor = 'nesw-resize';
                    break;
                default:
                    if (isMouseInCroppingArea(moveEvent)) {
                        current.style.cursor = 'move';
                    }
                    else {
                        current.style.cursor = 'default';
                    }
                    break;
            }
        };
        current === null || current === void 0 ? void 0 : current.addEventListener('mousedown', onMouseDown);
        current === null || current === void 0 ? void 0 : current.addEventListener('mousemove', onMouseMove);
        current === null || current === void 0 ? void 0 : current.addEventListener('touchstart', onMouseDown);
        current === null || current === void 0 ? void 0 : current.addEventListener('touchmove', onMouseMove);
        return function () {
            current === null || current === void 0 ? void 0 : current.removeEventListener('mousedown', onMouseDown);
            current === null || current === void 0 ? void 0 : current.removeEventListener('mousemove', onMouseMove);
            current === null || current === void 0 ? void 0 : current.removeEventListener('touchstart', onMouseDown);
            current === null || current === void 0 ? void 0 : current.removeEventListener('touchmove', onMouseMove);
        };
    }, [cropper, onCropped]);
    // 画像のトリミング
    useEffect(function () {
        var srcCanvas = canvasImageLayer.current;
        var dstCanvas = canvasOutputLayer.current;
        if (!srcCanvas || !dstCanvas) {
            return;
        }
        var _a = {
            x: Math.round(cropper.x),
            y: Math.round(cropper.y),
            width: Math.round(cropper.width),
            height: Math.round(cropper.height),
        }, x = _a.x, y = _a.y, width = _a.width, height = _a.height;
        if (width === 0 || height === 0) {
            return;
        }
        dstCanvas.width = width;
        dstCanvas.height = height;
        var srcCtx = srcCanvas.getContext('2d');
        var dstCtx = dstCanvas.getContext('2d');
        if (!srcCtx || !dstCtx) {
            return;
        }
        var imageData = srcCtx.getImageData(x, y, width, height);
        dstCtx.putImageData(imageData, 0, 0);
        onCropped === null || onCropped === void 0 ? void 0 : onCropped(dstCanvas.toDataURL(imageType));
    }, [cropper, imageType, onCropped]);
    return (React.createElement("div", { className: classes.root, ref: root },
        React.createElement("canvas", { className: classes.canvas, ref: canvasImageLayer }),
        React.createElement("canvas", { className: classes.canvas, ref: canvasCropperLayer }),
        React.createElement("canvas", { className: classes.outputCanvas, ref: canvasOutputLayer })));
};
